import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import DepartmentsPage from './pages/DepartmentsPage';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import InventoryPage from './pages/InventoryPage';
import ChemicalProductsPage from './pages/ChemicalProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
// Import condicional para manejar entradas multi-producto
import EntriesPageSingle from './pages/EntriesPage';
import EntriesPageMulti from './pages/EntriesPage-Multi';
// Se usará la versión correcta según la configuración del backend
const EntriesPage = EntriesPageMulti; // Cambia a EntriesPageSingle si es necesario
// Import condicional para manejar detalles de entradas multi-producto
import EntryDetailPageSingle from './pages/EntryDetailPage';
import EntryDetailPageMulti from './pages/EntryDetailPage-Multi';
// Se usará la versión correcta según la configuración del backend
const EntryDetailPage = EntryDetailPageMulti; // Cambia a EntryDetailPageSingle si es necesario
import ExitsPage from './pages/ExitsPage';
import ExitDetailPageSingle from './pages/ExitDetailPage';
import ExitDetailPageMulti from './pages/ExitDetailPageMulti';
// Se usará la versión multi-producto para las salidas
const ExitDetailPage = ExitDetailPageMulti; // Cambia a ExitDetailPageSingle si es necesario
import SummaryPage from './pages/SummaryPage';
import LoansPage from './pages/LoansPage';
import LoanDetailPage from './pages/LoanDetailPage';
import TrucksPage from './pages/TrucksPage';
import TruckDetailPage from './pages/TruckDetailPage';
import FleetStatusPage from './pages/FleetStatusPage'; // Importamos la nueva página de estado de flota
import MaintenanceSchedulePage from './pages/MaintenanceSchedulePage'; // Página de programa de mantenimientos
import UnitsPage from './pages/UnitsPage';
import DollysPage from './pages/DollysPage';
import SemiRemolquePage from './pages/SemiRemolquePage';
import AutoTanquesPage from './pages/AutoTanquesPage';
import CajaSecaPage from './pages/CajaSecaPage';
import UnidadesGasPage from './pages/UnidadesGasPage';
import GruasPage from './pages/GruasPage';
import MaintenancesPage from './pages/MaintenancesPage';
import MaintenanceDetailPage from './pages/MaintenanceDetailPage';
import FleetTrackingPage from './pages/FleetTrackingPage';
import RHDashboard from './pages/RHDashboard';
import RHEmpleados from './pages/RHEmpleados';
import RHExpedientes from './pages/RHExpedientes';
import RHEstadisticas from './pages/RHEstadisticas';
import RHFiniquitos from './pages/RHFiniquitos';
import RHVacaciones from './pages/RHVacaciones';

// Importar servicios
import { productsService } from './services/productsService';
import { suppliersService } from './services/suppliersService';
import { entriesService } from './services/entriesService';
import { exitsService } from './services/exitsService';
import { initApi } from './services/api';

export default function App() {
  // Estados principales
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [exits, setExits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Cargar datos desde MySQL al iniciar
  useEffect(() => {
    // Evitar múltiples ejecuciones
    if (initialized) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        setInitialized(true);
        console.log('🔄 Inicializando conexión al backend...');
        const init = await initApi();
        if (!init.success) {
          throw new Error('No se pudo conectar al backend');
        }

        console.log('🔄 Cargando datos desde MySQL...');
        // Cargar datos en paralelo
        const [suppliersData, productsData, entriesData, exitsData] = await Promise.all([
          suppliersService.getAll().catch(err => {
            console.warn('⚠️ Error cargando proveedores:', err);
            return { data: [] };
          }),
          productsService.getAllUnpaginated().catch(err => {
            console.warn('⚠️ Error cargando productos:', err);
            return { data: [] };
          }),
          entriesService.getAll().catch(err => {
            console.warn('⚠️ Error cargando entradas:', err);
            return { data: [] };
          }),
          exitsService.getAll().catch(err => {
            console.warn('⚠️ Error cargando salidas:', err);
            return { data: [] };
          })
        ]);

        // Extraer los arrays de datos del campo 'data'
        setSuppliers(suppliersData.data || []);
        setProducts(productsData.data || []);
        setEntries(entriesData.data || []);
        setExits(exitsData.data || []);
        
        console.log('✅ Datos cargados exitosamente');
        console.log(`📊 Proveedores: ${suppliersData.data?.length || 0}, Productos: ${productsData.data?.length || 0}`);

      } catch (error) {
        console.error('❌ Error cargando datos:', error);
        setError('Error al cargar los datos del sistema');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [initialized]);

  // Mostrar loading mientras carga
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-red-700 font-medium">Cargando Sistema de Almacén...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si hay problemas
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-600 text-xl mb-4">⚠️ Error del Sistema</div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  console.log('🚀 App iniciando con datos de MySQL...');

  return (
    <Router>
      <AuthProvider>
        <Layout>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedRoute><DepartmentsPage /></ProtectedRoute>} />
            <Route path="/departments" element={<ProtectedRoute><DepartmentsPage /></ProtectedRoute>} />
            <Route path="/almacen" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><InventoryPage products={products} setProducts={setProducts} /></ProtectedRoute>} />
            <Route path="/chemical-products" element={<ProtectedRoute><ChemicalProductsPage /></ProtectedRoute>} />
            <Route path="/inventory/product/:id" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><SuppliersPage suppliers={suppliers} setSuppliers={setSuppliers} /></ProtectedRoute>} />
            <Route path="/suppliers/supplier/:id" element={<ProtectedRoute><SupplierDetailPage /></ProtectedRoute>} />
            <Route path="/entries" element={<ProtectedRoute><EntriesPage products={products} suppliers={suppliers} entries={entries} setEntries={setEntries} /></ProtectedRoute>} />
            <Route path="/entries/entry/:id" element={<ProtectedRoute><EntryDetailPage /></ProtectedRoute>} />
            <Route path="/exits" element={<ProtectedRoute><ExitsPage products={products} exits={exits} setExits={setExits} /></ProtectedRoute>} />
            <Route path="/exits/exit/:id" element={<ProtectedRoute><ExitDetailPageMulti /></ProtectedRoute>} />
            <Route path="/exits/exit-multi/:id" element={<ProtectedRoute><ExitDetailPageMulti /></ProtectedRoute>} />
            <Route path="/summary" element={<ProtectedRoute><SummaryPage /></ProtectedRoute>} />
            <Route path="/loans" element={<ProtectedRoute><LoansPage /></ProtectedRoute>} />
            <Route path="/loans/:id" element={<ProtectedRoute><LoanDetailPage /></ProtectedRoute>} />
            <Route path="/trucks" element={<ProtectedRoute><TrucksPage /></ProtectedRoute>} />
            {/* Ruta específica para nueva unidad DEBE ir antes que las rutas dinámicas con parámetros */}
            <Route path="/trucks/new" element={<ProtectedRoute><TruckDetailPage /></ProtectedRoute>} />
            <Route path="/fleet" element={<ProtectedRoute><FleetStatusPage /></ProtectedRoute>} /> {/* Nueva ruta para el estado de la flota */}
            <Route path="/fleet-tracking" element={<ProtectedRoute><FleetTrackingPage /></ProtectedRoute>} /> {/* Registro diario de flotilla */}
            <Route path="/trucks/tractocamiones" element={<ProtectedRoute><UnitsPage tipoUnidad="Tractocamion" title="Tractocamiones" /></ProtectedRoute>} />
            <Route path="/trucks/auto-tanques" element={<ProtectedRoute><AutoTanquesPage /></ProtectedRoute>} />
            <Route path="/trucks/dollys" element={<ProtectedRoute><DollysPage /></ProtectedRoute>} />
            <Route path="/trucks/caja-seca" element={<ProtectedRoute><CajaSecaPage /></ProtectedRoute>} />
            <Route path="/trucks/unidades-gas" element={<ProtectedRoute><UnidadesGasPage /></ProtectedRoute>} />
            <Route path="/trucks/semiremolques" element={<ProtectedRoute><SemiRemolquePage /></ProtectedRoute>} />
            <Route path="/trucks/gruas" element={<ProtectedRoute><GruasPage /></ProtectedRoute>} />
            <Route path="/trucks/:id" element={<ProtectedRoute><TruckDetailPage /></ProtectedRoute>} />
            <Route path="/maintenances" element={<ProtectedRoute><MaintenancesPage /></ProtectedRoute>} />
            <Route path="/maintenances/:id" element={<ProtectedRoute><MaintenanceDetailPage /></ProtectedRoute>} />
            <Route path="/maintenance-schedule" element={<ProtectedRoute><MaintenanceSchedulePage /></ProtectedRoute>} />
            <Route path="/fleet-status" element={<ProtectedRoute><FleetStatusPage /></ProtectedRoute>} />
            <Route path="/recursos-humanos" element={<ProtectedRoute><RHDashboard /></ProtectedRoute>} />
            <Route path="/rh/empleados" element={<ProtectedRoute><RHEmpleados /></ProtectedRoute>} />
            <Route path="/rh/expedientes" element={<ProtectedRoute><RHExpedientes /></ProtectedRoute>} />
            <Route path="/rh/estadisticas" element={<ProtectedRoute><RHEstadisticas /></ProtectedRoute>} />
            <Route path="/rh/finiquitos" element={<ProtectedRoute><RHFiniquitos /></ProtectedRoute>} />
            <Route path="/rh/vacaciones" element={<ProtectedRoute><RHVacaciones /></ProtectedRoute>} />
          </Routes>
        </Layout>
      </AuthProvider>
    </Router>
  );
}
